import { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import type { ReactNode, RefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePreview } from './usePreview';
import { useRealtimePreview } from './useRealtimePreview';

interface GlobalPreviewContextType {
  // Preview state
  previewState: string;
  previewError: string | null;
  cacheBust: number;
  triggerPreview: () => void;
  refreshIframe: () => void;

  // Realtime updates
  iframeRef: RefObject<HTMLIFrameElement | null>;
  sendUpdate: (cmsType: string, field: string, value: unknown, richtext?: boolean) => void;
  sendMediaUpdate: (cmsType: string, mediaField: string, url: string) => void;
  injectDesignVariables: (vars: Record<string, unknown>) => void;
  scrollToSection: (section: string) => void;
  scrollToField: (cmsType: string, field?: string, cmsId?: string) => void;
  toggleSection: (section: string, visible: boolean) => void;
  setEditMode: (enabled: boolean) => void;
  highlightField: (cmsType: string | null, field?: string, cmsId?: string) => void;
  highlightBySelector: (selector: string | null) => void;
  replayQueue: () => void;
  clearQueue: () => void;

  // Panel state
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  currentPage: string;
  setCurrentPage: (page: string) => void;
  previewDirty: boolean;
  setPreviewDirty: (dirty: boolean) => void;

  // Site info
  subdomain: string | null;
  siteId: string | undefined;
}

export const GlobalPreviewContext = createContext<GlobalPreviewContextType | null>(null);

export function GlobalPreviewProvider({
  siteId,
  subdomain,
  children,
}: {
  siteId: string | undefined;
  subdomain: string | null;
  children: ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState('/');
  const [previewDirty, setPreviewDirty] = useState(false);
  const navigate = useNavigate();

  const {
    previewState,
    previewError,
    cacheBust,
    triggerPreview,
    refreshIframe,
  } = usePreview(siteId);

  const {
    iframeRef,
    sendUpdate,
    sendMediaUpdate,
    injectDesignVariables,
    scrollToSection,
    scrollToField,
    toggleSection,
    setEditMode,
    highlightField,
    highlightBySelector,
    replayQueue,
    clearQueue,
  } = useRealtimePreview();

  // Auto-open preview when navigating to an editor page
  // Auto-trigger first build
  const hasTriggered = useRef(false);
  useEffect(() => {
    if (isOpen && !hasTriggered.current && previewState === 'idle') {
      hasTriggered.current = true;
      triggerPreview();
    }
  }, [isOpen, previewState, triggerPreview]);

  // Reset when site changes (but not on initial mount)
  const prevSiteId = useRef(siteId);
  useEffect(() => {
    if (prevSiteId.current && prevSiteId.current !== siteId) {
      hasTriggered.current = false;
      setIsOpen(false);
      setCurrentPage('/');
    }
    prevSiteId.current = siteId;
  }, [siteId]);

  // Enable click-to-edit in the iframe when preview is ready and open
  useEffect(() => {
    setEditMode(previewState === 'ready' && isOpen);
  }, [previewState, isOpen, setEditMode]);

  // Map cmsType + optional cmsId → CMS editor route
  const resolveEditorRoute = useCallback((cmsType: string, cmsId?: string | null): string | null => {
    if (!siteId) return null;
    const base = `/sites/${siteId}/content`;
    const listRoutes: Record<string, string> = {
      product: 'products',
      category: 'categories',
      menu: 'menu',
      testimonial: 'testimonials',
      hours: 'hours',
      post: 'blog',
      blog: 'blog',
    };
    const singleRoutes: Record<string, string> = {
      hero: 'hero',
      about: 'about',
      contact: 'contact',
      cta: 'cta',
      site: 'site',
      gallery: 'gallery',
    };
    if (listRoutes[cmsType]) {
      return cmsId ? `${base}/${listRoutes[cmsType]}/${cmsId}` : `${base}/${listRoutes[cmsType]}`;
    }
    if (singleRoutes[cmsType]) {
      return `${base}/${singleRoutes[cmsType]}`;
    }
    return null;
  }, [siteId]);

  // Listen for click-to-edit messages from the preview iframe
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      const d = e.data;
      if (!d || d.type !== 'cms:elementSelected') return;
      const route = resolveEditorRoute(d.cmsType, d.cmsId);
      if (route) navigate(route);
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [resolveEditorRoute, navigate]);

  const value: GlobalPreviewContextType = {
    previewState,
    previewError,
    cacheBust,
    triggerPreview,
    refreshIframe,
    iframeRef,
    sendUpdate,
    sendMediaUpdate,
    injectDesignVariables,
    scrollToSection,
    scrollToField,
    toggleSection,
    setEditMode,
    highlightField,
    highlightBySelector,
    replayQueue,
    clearQueue,
    isOpen,
    setIsOpen,
    currentPage,
    setCurrentPage,
    previewDirty,
    setPreviewDirty,
    subdomain,
    siteId,
  };

  return (
    <GlobalPreviewContext.Provider value={value}>
      {children}
    </GlobalPreviewContext.Provider>
  );
}

export function useGlobalPreview() {
  const ctx = useContext(GlobalPreviewContext);
  if (!ctx) {
    throw new Error('useGlobalPreview must be used within GlobalPreviewProvider');
  }
  return ctx;
}
